<template>
  <div class="identity-welcome">
    <a href="https://getcommunal.com" class="identity-welcome__brand">
      <img
        class="identity-welcome__logo"
        src="/images/communal-logo.svg"
        alt="Communal"
        height="36"
      />
    </a>

    <div class="identity-welcome__copy">
      <p class="identity-welcome__kicker">{{ kicker }}</p>
      <h2 class="identity-welcome__headline">{{ headline }}</h2>
      <p class="identity-welcome__subtitle">{{ subtitle }}</p>
    </div>

    <div class="identity-welcome__cta">
      <p class="identity-welcome__cta-copy">
        New to Communal? See it before you sign up. We'll walk you through a
        real workflow, no canned tour and no sales script.
      </p>
      <a class="identity-welcome__cta-link" href="https://getcommunal.com/demo">
        <span>Book a demo</span>
        <span class="identity-welcome__cta-arrow" aria-hidden="true">→</span>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
const {
  kicker = 'Sign in',
  headline = 'Welcome back.',
  subtitle = "Pick up right where you left off. Find your organization and we'll hand you off to the right place.",
} = defineProps<{
  headline?: string;
  kicker?: string;
  subtitle?: string;
}>();
</script>

<style lang="scss" scoped>
$ink: #111827;
$muted: #6b7280;
$line: #e5e7eb;
$brand-blue: #1984c4;
$brand-blue-deep: #14365b;

.identity-welcome {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;

  @media (min-width: 992px) {
    gap: 2rem;
    min-height: 24rem;
  }
}

.identity-welcome__brand {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.identity-welcome__logo {
  height: 34px;
  width: auto;

  @media (min-width: 992px) {
    height: 40px;
  }
}

.identity-welcome__copy {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.identity-welcome__kicker {
  margin: 0;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: $muted;
}

.identity-welcome__headline {
  margin: 0;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: clamp(2.25rem, 5.25vw, 3.5rem);
  font-weight: 800;
  line-height: 1.02;
  letter-spacing: -0.034em;
  color: $ink;
  // No nowrap: longer headlines (e.g. "Pick a place to land.") would otherwise
  // overflow the masthead column and slide under the right-hand card on lg+.
  text-wrap: balance;
  overflow-wrap: break-word;
}

.identity-welcome__subtitle {
  margin: 0.25rem 0 0;
  max-width: 36ch;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 1.0625rem;
  line-height: 1.55;
  color: $muted;
  text-wrap: pretty;
}

// CTA block: marketing-site stamp pill + ledger-style microcopy + a clear
// "Book a demo" call-to-action that points new visitors to the demo page.
// Sits in the masthead's lower half on lg+ viewports, hidden below.
.identity-welcome__cta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.875rem;
  margin-top: auto;
  padding-top: 1.75rem;
  border-top: 1px solid rgba(20, 54, 91, 0.1);

  @media (max-width: 991.98px) {
    display: none;
  }
}

.identity-welcome__cta-copy {
  margin: 0;
  max-width: 36ch;
  font-size: 0.875rem;
  line-height: 1.5;
  color: $muted;
  text-wrap: pretty;
}

.identity-welcome__cta-link {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.625rem 1.05rem;
  background: $ink;
  border-radius: 999px;
  color: #fff;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 0.8125rem;
  font-weight: 600;
  letter-spacing: 0.005em;
  text-decoration: none;
  box-shadow: 0 6px 16px -8px rgba(20, 54, 91, 0.45);
  transition:
    transform 180ms ease,
    box-shadow 180ms ease,
    background 180ms ease;

  &:hover,
  &:focus-visible {
    background: $brand-blue-deep;
    transform: translateY(-1px);
    box-shadow: 0 10px 22px -10px rgba(20, 54, 91, 0.55);
    color: #fff;
  }
}

.identity-welcome__cta-arrow {
  display: inline-block;
  transition: transform 180ms ease;
  font-weight: 500;

  .identity-welcome__cta-link:hover &,
  .identity-welcome__cta-link:focus-visible & {
    transform: translateX(2px);
  }
}
</style>
